"use client";

import React from "react";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";

/**
 * SolutionForAgenciesAndFreelancers — Section 4: Money-Math Hero
 * Drop-in für: src/pages/SolutionForAgenciesAndFreelancers/sections/MoneyMathMarge.tsx
 *
 * Der Killer-Hook für Agenturen: CHF 15 Kosten → CHF 50-100 Verkauf →
 * CHF 700-1'700 Marge / Monat bei 20 Kunden.
 */

export default function MoneyMathMarge() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  return <section id="money-math" className="bg-secondary py-20 md:py-24">
      <div ref={ref} className={`max-w-6xl mx-auto px-4 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground mb-4 leading-tight">
            Ihre Marge, schwarz auf weiss.
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground">
            Beispielrechnung mit dem 20er-Paket bei 20 Kunden.
          </p>
        </div>

        <div className="relative bg-gradient-to-br from-primary/5 via-primary/[0.03] to-transparent border border-primary/20 rounded-2xl md:rounded-3xl p-6 md:p-10 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
          backgroundImage: "radial-gradient(circle, var(--color-foreground) 1px, transparent 1px)",
          backgroundSize: "20px 20px"
        }} />

          <div className="relative grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] gap-4 md:gap-2 items-stretch">
            <div className="bg-background border border-border rounded-xl p-5 md:p-6 text-center">
              <div className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase mb-2">
                Sie zahlen
              </div>
              <div className="text-3xl md:text-4xl font-bold text-foreground mb-1 leading-none">
                ~CHF 15
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                pro Website / Monat
              </div>
            </div>

            <div className="flex items-center justify-center text-muted-foreground/40 py-2 md:py-0">
              <ArrowRight className="w-5 h-5 hidden md:block" />
              <ArrowDown className="w-5 h-5 md:hidden" />
            </div>

            <div className="bg-background border border-border rounded-xl p-5 md:p-6 text-center">
              <div className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase mb-2">
                Sie verrechnen
              </div>
              <div className="text-3xl md:text-4xl font-bold text-foreground mb-1 leading-none">
                CHF 50–100
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                pro Website / Monat
              </div>
            </div>

            <div className="flex items-center justify-center text-muted-foreground/40 py-2 md:py-0">
              <ArrowRight className="w-5 h-5 hidden md:block" />
              <ArrowDown className="w-5 h-5 md:hidden" />
            </div>

            <div className="bg-primary text-white rounded-xl p-5 md:p-6 text-center shadow-lg shadow-primary/20 relative">
              <div className="text-[10px] font-semibold tracking-wider text-white/80 uppercase mb-2">
                Ihre Marge
              </div>
              <div className="text-3xl md:text-4xl font-bold mb-1 leading-none">
                CHF 700–1'700
              </div>
              <div className="text-xs text-white/80 mt-2">
                pro Monat · 20 Kunden
              </div>
            </div>
          </div>

          <p className="text-center text-xs text-muted-foreground mt-6 md:mt-8">
            Skalieren Sie auf 50+ Kunden — wir bauen Ihnen ein individuelles
            Paket.
          </p>
        </div>
      </div>
    </section>;
}